/** «Диалоги» before the app's code: on a new connection the code (~270 KB) comes 0.9 s after the page (18.10.2026), and
 * only then did the app start its first reads and the page's own code. boot.ts — a tiny separate script of the page,
 * before the app's code, with nothing shared with it — runs this: right after the page it asks again the GET reads the
 * app made at its last start on this address (remembered below, paths only), and the app's own fetch of the very same
 * path takes that answer, once, when it is a success; with the same build it also preloads the code the start loaded
 * after the app's (the page, its icons). Every other request goes to the network as usual. The app knows only the names
 * below (dialogs-list-reads.ts repeats them; a test keeps them equal). */
export const STARTUP_READS_KEY = "vekha.startup.reads";
/** The dialog list dispatches it when its first list is on screen: the reads made until then are the start's. */
export const STARTUP_READY = "vekha:startup-ready";
/** main.tsx sets it before its first read: if the app's code ran first, this script stays out (no read twice). */
export const APP_STARTED = "vekhaAppStarted";
const KEEP_MS = 10_000;
const MAX_READS = 10;
const MAX_MODULES = 40;
/** A session idles out after 12 h without requests (api service.ts SESSION_IDLE_MS): a start older than this one is not
 * replayed, so the morning's login page is not asked anything it would refuse. */
export const STARTUP_FRESH_MS = 11 * 60 * 60 * 1_000;
const MODULE = /^\/assets\/[^/?#]+\.(?:js|css)$/u;

// `addresses`: where the start began and where its list showed (the root leads on to «Диалоги», which add the agent
// and scenario): a reload of either starts the same way. A start that named no agent nor scenario got the default
// ones, as the list's address without them would.
type Remembered = Readonly<{ addresses: readonly string[]; at: number; paths: readonly string[]; build?: string; modules?: readonly string[] }>;
type Page = Readonly<{
  document: Pick<Document, "querySelectorAll" | "createElement" | "head">;
  performance: Pick<Performance, "getEntriesByType">;
}>;

/** The start's address: «Диалоги» or the root leading to them, without the open deal (it does not change these reads). */
export function startupAddress(pathname: string, search: string): string | null {
  if (!/^\/(?:dialogs\/*)?$/u.test(pathname)) return null;
  const params = new URLSearchParams(search);
  // A search names a person: such an address is not remembered.
  if (params.has("q")) return null;
  params.delete("deal");
  params.delete("conversation");
  params.sort();
  const query = params.toString();
  return `${pathname}${query ? `?${query}` : ""}`;
}

export function installStartupReads(
  scope: Pick<Window, "fetch" | "addEventListener" | "setTimeout"> & { location: Pick<Location, "pathname" | "search"> } & Page,
  storage: Pick<Storage, "getItem" | "setItem">,
) {
  const address = startupAddress(scope.location.pathname, scope.location.search);
  if (address === null || Reflect.get(scope, APP_STARTED) === true) return;
  const startParams = new URLSearchParams(scope.location.search);
  const defaultContext = !startParams.has("agent") && !startParams.has("scenario");
  // The build is the app's entry; the page's own scripts and preloads are not the start's later code.
  const own = [...scope.document.querySelectorAll<HTMLScriptElement | HTMLLinkElement>("script[src], link[href]")]
    .map((element) => element.getAttribute("src") ?? element.getAttribute("href") ?? "");
  const build = own.find((src) => /^\/assets\/index-[^/]+\.js$/u.test(src)) ?? "";
  const original = scope.fetch;
  const network = original.bind(scope);
  const early = new Map<string, Promise<Response>>();
  try {
    const remembered = JSON.parse(storage.getItem(STARTUP_READS_KEY) ?? "null") as Remembered | null;
    const fresh = typeof remembered?.at === "number" && Date.now() - remembered.at < STARTUP_FRESH_MS;
    if (Array.isArray(remembered?.addresses) && remembered.addresses.includes(address) && fresh && Array.isArray(remembered.paths)) {
      for (const path of remembered.paths.slice(0, MAX_READS)) {
        if (typeof path !== "string" || !path.startsWith("/api/") || early.has(path)) continue;
        const answer = network(path, { credentials: "include", headers: { Accept: "application/json" } });
        answer.catch(() => undefined);
        early.set(path, answer);
      }
      if (build !== "" && remembered.build === build && Array.isArray(remembered.modules)) {
        for (const module of remembered.modules.slice(0, MAX_MODULES)) {
          if (typeof module !== "string" || !MODULE.test(module)) continue;
          // As the app's own loader asks them (crossorigin), so it takes these instead of asking again.
          const link = scope.document.createElement("link");
          if (module.endsWith(".css")) {
            link.rel = "preload";
            link.as = "style";
          } else {
            link.rel = "modulepreload";
          }
          link.crossOrigin = "";
          link.href = module;
          scope.document.head.append(link);
        }
      }
    }
  } catch { /* nothing remembered: the app asks as usual */ }

  let recording: string[] | null = [];
  // After the start nothing is served early: a later read is always fresh.
  const done = () => { recording = null; early.clear(); scope.fetch = original; };
  scope.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    const path = typeof input === "string" && input.startsWith("/api/")
      && (init?.method ?? "GET").toUpperCase() === "GET" && !init?.body ? input : null;
    if (path === null) return network(input, init);
    if (recording !== null && recording.length < MAX_READS) recording.push(path);
    const answer = early.get(path);
    if (!answer) return network(input, init);
    early.delete(path);
    // An early answer refused for the session (asked before signing in) or failed on the server is asked again; any
    // other is the answer. A timeout of the asker still ends its wait.
    const asked = answer.then(
      (response) => response.status === 401 || response.status >= 500 ? network(input, init) : response,
      () => network(input, init),
    );
    const signal = init?.signal;
    if (!signal) return asked;
    return new Promise<Response>((resolve, reject) => {
      if (signal.aborted) return reject(signal.reason);
      signal.addEventListener("abort", () => reject(signal.reason), { once: true });
      asked.then(resolve, reject);
    });
  };
  scope.addEventListener(STARTUP_READY, () => {
    if (recording !== null && recording.length > 0) {
      const modules = [...new Set(scope.performance.getEntriesByType("resource")
        .map((entry) => { try { return new URL(entry.name).pathname; } catch { return ""; } })
        .filter((module) => MODULE.test(module) && !own.includes(module)))].slice(0, MAX_MODULES);
      try {
        const shownParams = new URLSearchParams(scope.location.search);
        const shown = startupAddress(scope.location.pathname, shownParams.toString());
        shownParams.delete("agent");
        shownParams.delete("scenario");
        const shownByDefault = defaultContext ? startupAddress(scope.location.pathname, shownParams.toString()) : null;
        const addresses = [...new Set([address, shown, shownByDefault].filter((item): item is string => item !== null))];
        storage.setItem(STARTUP_READS_KEY, JSON.stringify({ addresses, at: Date.now(), paths: recording, build, modules } satisfies Remembered));
      } catch { /* not remembered */ }
    }
    done();
  }, { once: true });
  // A start that never shows the list (the login page, another page): nothing is remembered, nothing is kept.
  scope.setTimeout(done, KEEP_MS);
}
